/**
 * As chamadas do acesso do inscrito (F04) — CPF ou gov.br, código, senha e
 * sessão. Contrato em `docs/credenciamento-ponta-a-ponta/frentes/F04.md`.
 *
 * O inscrito não tem conta no Keycloak (D-10): a sessão é um cookie
 * `HttpOnly` que o navegador guarda e devolve sozinho. Por isso toda chamada
 * é `publica` (sem token) e com `credenciais` (manda o cookie). Nenhum token,
 * CPF completo ou senha fica em memória da tela depois da chamada.
 */

import { useQuery } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';

const BASE = '/bff/v1/inscrito-acesso';

export type FinalidadeDoFluxo = 'CADASTRO' | 'RECUPERACAO';
export type CanalDoCodigo = 'EMAIL' | 'SMS';
export type ViaDoFluxo = 'CODIGO' | 'GOVBR';

export interface CodigoDoFluxo {
  canal: CanalDoCodigo;
  destino: string;
  expiraEm: string;
  reenvioLiberadoEm: string;
  reenviosRestantes: number;
  tentativasRestantes: number;
  bloqueadoAte: string | null;
}

export interface EstadoDoFluxo {
  etapa: 'CANAL' | 'CODIGO' | 'SENHA';
  finalidade: FinalidadeDoFluxo;
  via: ViaDoFluxo | null;
  canais: { canal: CanalDoCodigo; destino: string }[];
  codigo: CodigoDoFluxo | null;
  contaExistente: boolean | null;
  validadeDoCodigoEmMinutos: number;
  expiraEm: string;
  agora: string;
}

export interface InscritoPublico {
  nome: string | null;
  cpfMascarado: string;
}

export type RespostaDeAbertura =
  | { resultado: 'CONTATOS'; fluxo: EstadoDoFluxo }
  | { resultado: 'PROCURAR_DTP' }
  | { resultado: 'SGTP_INDISPONIVEL' };

export type ResultadoDoEnvio =
  | 'ENVIADO'
  | 'AGUARDE_REENVIO'
  | 'REENVIOS_ESGOTADOS'
  | 'BLOQUEADO'
  | 'CANAL_INDISPONIVEL'
  | 'JA_VERIFICADO';

export type ResultadoDaConferencia =
  'CONFERIDO' | 'INCORRETO' | 'EXPIRADO' | 'BLOQUEADO' | 'SEM_CODIGO';

export type RespostaDaSenha =
  | { resultado: 'DEFINIDA'; inscrito: InscritoPublico }
  | { resultado: 'SENHA_FRACA'; requisitos: string[] }
  | { resultado: 'CONFIRMACAO_DIFERENTE' };

export type RespostaDoGovbr =
  | { resultado: 'CONFIRMADO'; fluxo: EstadoDoFluxo }
  | { resultado: 'PENDENTE' }
  | { resultado: 'RECUSADO' }
  | { resultado: 'PROCURAR_DTP' }
  | { resultado: 'SGTP_INDISPONIVEL' }
  | { resultado: 'GOVBR_INDISPONIVEL' };

function postar<T>(caminho: string, corpo: unknown = {}): Promise<T> {
  return chamarApi<T>(`${BASE}${caminho}`, {
    metodo: 'POST',
    corpo,
    publica: true,
    credenciais: true,
  });
}

export const apiDoInscrito = {
  abrirFluxo: (cpf: string, finalidade: FinalidadeDoFluxo) =>
    postar<RespostaDeAbertura>('/fluxo', { cpf, finalidade }),
  enviarCodigo: (canal: CanalDoCodigo) =>
    postar<{ resultado: ResultadoDoEnvio; fluxo: EstadoDoFluxo }>('/fluxo/codigo', { canal }),
  conferirCodigo: (codigo: string) =>
    postar<{ resultado: ResultadoDaConferencia; fluxo: EstadoDoFluxo }>('/fluxo/codigo/conferir', {
      codigo,
    }),
  definirSenha: (senha: string, confirmacao: string) =>
    postar<RespostaDaSenha>('/fluxo/senha', { senha, confirmacao }),
  entrarPeloFluxo: () => postar<{ inscrito: InscritoPublico }>('/fluxo/entrar'),
  iniciarGovbr: () =>
    postar<{ resultado: 'PENDENTE' } | { resultado: 'GOVBR_INDISPONIVEL' }>('/govbr'),
  confirmarGovbr: () => postar<RespostaDoGovbr>('/govbr/confirmar'),
  entrar: (cpf: string, senha: string) =>
    postar<{ inscrito: InscritoPublico }>('/entrar', { cpf, senha }),
  lerSessao: () =>
    chamarApi<{ inscrito: InscritoPublico; sessaoExpiraEm: string }>(`${BASE}/sessao`, {
      publica: true,
      credenciais: true,
    }),
  sair: () => postar<void>('/sair'),
};

/** A chave da sessão no cache — quem entra ou sai invalida por ela. */
export const CHAVE_DA_SESSAO_DO_INSCRITO = ['inscrito-acesso', 'sessao'] as const;

/**
 * Quem está na sessão do inscrito, para as telas das frentes seguintes (F05,
 * F06). `data === null` quando não há sessão (401); outro erro vem como erro.
 *
 * `habilitada: false` não pergunta ao servidor — o cabeçalho do portal usa
 * isso quando quem navega já tem sessão do Keycloak.
 */
export function useSessaoDoInscrito({ habilitada = true }: { habilitada?: boolean } = {}) {
  return useQuery({
    queryKey: CHAVE_DA_SESSAO_DO_INSCRITO,
    enabled: habilitada,
    queryFn: async () => {
      try {
        return await apiDoInscrito.lerSessao();
      } catch (erro) {
        if (erro instanceof ErroApi && erro.situacao === 401) return null;
        throw erro;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

/** Encerra a sessão no servidor; o cookie é apagado pela própria resposta. */
export function sairDoInscrito(): Promise<void> {
  return apiDoInscrito.sair();
}
